
'use client';

import * as React from 'react';
import { Trash, Info, ExternalLink, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CurrencyPriceInput, type CurrencyOptionsType } from '../currency-price-input';
import type { Product } from '@tagadapay/crm-entities';
import { sellingPlanFirstChargeAmount } from '@/lib/utils/subscription-price';
import { formatMoney } from '@/lib/money';

export interface PriceFormSchemaType {
  id?: string;
  accountId?: string;
  productId?: string;
  variantId?: string;
  default?: boolean;
  currencyOptions: CurrencyOptionsType;
  recurring?: boolean;
  billingTiming?: string;
  interval?: string | null;
  intervalCount?: number;
  rebillWithOrder?: boolean;
  rebillMode?: 'auth' | 'capture' | null;
  rebillStepIntervalMs?: number | null;
  isExternalSellingPlan?: boolean;
  priceSettings?: Product.PriceSettings | null;
  addDeliveryOnRebill?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

const DAYS = 24 * 60 * 60 * 1000;

const DEFAULT_PRICE_VALUES: Partial<PriceFormSchemaType> = {
  interval: 'month',
  intervalCount: 1,
  recurring: false,
  currencyOptions: {},
};

interface ProductPricesFormProps {
  prices: PriceFormSchemaType[];
  onUpdatePrices: (prices: PriceFormSchemaType[]) => void;
  variantIndex: number;
  storeId: string;
  hideAddPrice?: boolean;
  hideRemovePrice?: boolean;
}

export const ProductPricesForm = ({
  prices,
  onUpdatePrices,
  variantIndex,
  storeId,
  hideAddPrice = false,
  hideRemovePrice = false,
}: ProductPricesFormProps) => {
  const { t } = useTranslation('products');

  const handleAddPrice = () => {
    const isFirstPrice = prices.length === 0;
    const newPrice = {
      ...DEFAULT_PRICE_VALUES,
      default: isFirstPrice,
    } as PriceFormSchemaType;
    onUpdatePrices([...prices, newPrice]);
  };

  const handleRemovePrice = (index: number) => {
    const updatedPrices = prices.filter((_, i) => i !== index);

    if (updatedPrices.length > 0 && prices[index].default) {
      updatedPrices[0] = { ...updatedPrices[0], default: true };
    }

    onUpdatePrices(updatedPrices);
  };

  const handleUpdatePrice = React.useCallback(
    (index: number, data: Record<string, string | boolean | number | null | CurrencyOptionsType>) => {
      const updatedPrices = [...prices];
      updatedPrices[index] = { ...updatedPrices[index], ...data } as PriceFormSchemaType;

      if (data.default === true) {
        updatedPrices.forEach((price, i) => {
          if (i !== index) {
            price.default = false;
          }
        });
      }
      onUpdatePrices(updatedPrices);
    },
    [prices, onUpdatePrices],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-medium">{t('priceForm.prices')}</h5>
        {!hideAddPrice && (
          <Button type="button" onClick={handleAddPrice} size="sm" variant="outline" className="h-8 text-xs">
            {t('priceForm.addPrice')}
          </Button>
        )}
      </div>

      {prices.length === 0 && (
        <div className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
          <p>{t('priceForm.noPrices')}</p>
        </div>
      )}

      <div className="space-y-3">
        {prices.map((price, index) => (
          <PriceFormFields
            key={index}
            values={price}
            onChange={(data) => handleUpdatePrice(index, data)}
            onRemove={() => handleRemovePrice(index)}
            canRemove={!hideRemovePrice && !price.default}
            variantIndex={variantIndex}
            priceIndex={index}
            storeId={storeId}
          />
        ))}
      </div>
    </div>
  );
};

const PriceFormFields = React.memo(
  ({
    values,
    onChange,
    onRemove,
    canRemove,
    variantIndex,
    priceIndex,
    storeId,
  }: {
    values: PriceFormSchemaType;
    onChange: (data: Record<string, string | boolean | number | null | CurrencyOptionsType>) => void;
    onRemove: () => void;
    canRemove: boolean;
    variantIndex: number;
    priceIndex: number;
    storeId: string;
  }) => {
    const { t } = useTranslation('products');
    const fieldsId = `price-${variantIndex}-${priceIndex}`;
    const isExternalSellingPlan = values.isExternalSellingPlan === true;
    const firstChargeNotices = React.useMemo(
      () =>
        Object.entries(values.currencyOptions ?? {}).flatMap(([currency, option]) => {
          const charged = sellingPlanFirstChargeAmount(option.amount, values.priceSettings);
          return charged === null || charged === option.amount
            ? []
            : [{ currency, stored: option.amount, charged }];
        }),
      [values.currencyOptions, values.priceSettings],
    );

    return (
      <Card className="relative border-2 p-4 transition-all hover:shadow-md">
        <CardContent className="space-y-4 p-0">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {values.default && (
                <Badge variant="default" className="h-5 text-xs">
                  {t('default')}
                </Badge>
              )}
              {values.id && <CopyableResourceBadge name={t('priceId')} value={values.id} variant="outline" />}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id={`${fieldsId}-default`}
                checked={values.default ?? false}
                disabled={values.default ?? false}
                onCheckedChange={(checked) => onChange({ default: checked })}
              />
              <Label htmlFor={`${fieldsId}-default`} className="cursor-pointer text-xs font-medium">
                {t('priceForm.setAsDefault')}
              </Label>
            </div>
          </div>

          {/* External Selling Plan Warning */}
          {isExternalSellingPlan && (
            <Alert variant="destructive" className="border-amber-500 bg-amber-50 text-amber-900">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-900">
                {t('priceForm.externalSellingPlan.title')}
              </AlertTitle>
              <AlertDescription className="text-amber-800">
                {t('priceForm.externalSellingPlan.description')}{' '}
                <a
                  href="https://admin.shopify.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium underline"
                >
                  {t('priceForm.externalSellingPlan.shopifyAdmin')}
                  <ExternalLink className="h-3 w-3" />
                </a>
                .
              </AlertDescription>
            </Alert>
          )}

          {/* Pricing Type Section */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">{t('priceForm.pricingType')}</Label>
            <div className="space-y-2">
              <Select
                value={values.recurring ? 'Recurring' : 'One-time'}
                disabled={isExternalSellingPlan}
                onValueChange={(value) => {
                  if (value === 'Recurring') {
                    onChange({
                      recurring: true,
                      interval: 'month',
                      intervalCount: 1,
                      rebillWithOrder: false,
                      rebillMode: 'capture',
                      addDeliveryOnRebill: false,
                      rebillStepIntervalMs: null,
                    });
                  } else {
                    onChange({ recurring: false, rebillMode: null, rebillStepIntervalMs: null });
                  }
                }}
              >
                <SelectTrigger id={`${fieldsId}-type`} className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Recurring">{t('recurring')}</SelectItem>
                  <SelectItem value="One-time">{t('oneTime')}</SelectItem>
                </SelectContent>
              </Select>

              {values.recurring && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">{t('priceForm.billEvery')}</span>
                  <div className="w-16">
                    <Input
                      id={`${fieldsId}-cycle`}
                      type="number"
                      min={1}
                      className="h-9"
                      disabled={isExternalSellingPlan}
                      value={values.intervalCount || ''}
                      onChange={(event) =>
                        onChange({
                          intervalCount: parseInt(event.target.value) || 1,
                        })
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <Select
                      value={values.interval ?? 'month'}
                      disabled={isExternalSellingPlan}
                      onValueChange={(value) => onChange({ interval: value })}
                    >
                      <SelectTrigger id={`${fieldsId}-interval`} className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">{t('priceForm.day')}</SelectItem>
                        <SelectItem value="week">{t('priceForm.week')}</SelectItem>
                        <SelectItem value="month">{t('priceForm.month')}</SelectItem>
                        <SelectItem value="year">{t('priceForm.year')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recurring Options Section */}
          {values.recurring && (
            <div className="bg-muted/30 space-y-3 rounded-md border p-3">
              <Label className="text-xs font-medium">{t('priceForm.recurringOptions')}</Label>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id={`${fieldsId}-use-rebill-with-order`}
                    checked={values.rebillWithOrder === true}
                    onCheckedChange={(checked) => {
                      onChange({ rebillWithOrder: checked });
                    }}
                  />
                  <Label
                    htmlFor={`${fieldsId}-use-rebill-with-order`}
                    className="flex cursor-pointer items-center text-sm"
                  >
                    {t('priceForm.sendOrderToStorefront')}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="text-muted-foreground ml-1.5 h-3.5 w-3.5" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('priceForm.sendOrderToStorefrontTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id={`${fieldsId}-add-delivery-on-rebill`}
                    checked={values.addDeliveryOnRebill === true}
                    onCheckedChange={(checked) => {
                      onChange({ addDeliveryOnRebill: checked });
                    }}
                  />
                  <Label
                    htmlFor={`${fieldsId}-add-delivery-on-rebill`}
                    className="flex cursor-pointer items-center text-sm"
                  >
                    {t('priceForm.addDeliveryOnRebill')}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="text-muted-foreground ml-1.5 h-3.5 w-3.5" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('priceForm.addDeliveryOnRebillTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id={`${fieldsId}-use-auth`}
                    checked={values.rebillMode === 'auth'}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onChange({ rebillMode: 'auth', rebillStepIntervalMs: 3 * DAYS });
                      } else {
                        onChange({ rebillMode: 'capture', rebillStepIntervalMs: null });
                      }
                    }}
                  />
                  <Label
                    htmlFor={`${fieldsId}-use-auth`}
                    className="flex cursor-pointer items-center text-sm"
                  >
                    {t('priceForm.authorizeFirst')}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="text-muted-foreground ml-1.5 h-3.5 w-3.5" />
                        </TooltipTrigger>
                        <TooltipContent>{t('priceForm.authorizeFirstTooltip')}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>

                {values.rebillMode === 'auth' && values.rebillStepIntervalMs && (
                  <div className="space-y-2 pt-2">
                    <Label htmlFor={`${fieldsId}-rebill-step-interval`} className="text-xs">
                      {t('priceForm.minIntervalAuthCapture')}
                    </Label>
                    <RebillStepIntervalForm
                      id={`${fieldsId}-rebill-step-interval`}
                      value={values.rebillStepIntervalMs}
                      onChange={(rebillStepIntervalMs) => onChange({ rebillStepIntervalMs })}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Currency Prices Section */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">{t('priceForm.currencyPrices')}</Label>
            <div className="bg-muted/30 rounded-md border p-3">
              <CurrencyPriceInput
                values={values.currencyOptions}
                onChange={onChange}
                storeId={storeId}
                disabled={isExternalSellingPlan}
              />
              {firstChargeNotices.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {firstChargeNotices.map(({ currency, stored, charged }) => (
                    <p key={currency} className="text-muted-foreground text-xs">
                      {t('priceForm.externalSellingPlan.firstCharge', {
                        stored: formatMoney(stored, currency),
                        charged: formatMoney(charged, currency),
                      })}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>

        {canRemove && !isExternalSellingPlan && (
          <Button
            variant="destructive"
            onClick={onRemove}
            type="button"
            size="icon"
            className="absolute -right-2 -top-2 h-7 w-7 rounded-full shadow-md"
          >
            <Trash className="h-3.5 w-3.5" />
          </Button>
        )}
      </Card>
    );
  },
);

PriceFormFields.displayName = 'PriceFormFields';

// RebillStepIntervalForm component
function RebillStepIntervalForm(props: { id: string; value: number; onChange: (value: number) => unknown }) {
  const { t } = useTranslation('products');
  const SECONDS = 1_000;
  const MINUTES = 60 * SECONDS;
  const HOURS = 60 * MINUTES;
  const DAYS_MS = 24 * HOURS;

  const { humanValue, unit } = React.useMemo(() => {
    if (props.value >= 1 * DAYS_MS && props.value % DAYS_MS === 0)
      return { humanValue: Math.round(props.value / DAYS_MS), unit: 'days' as const };

    if (props.value >= 1 * HOURS && props.value % HOURS === 0)
      return { humanValue: Math.round(props.value / HOURS), unit: 'hours' as const };

    return { humanValue: Math.round(props.value / MINUTES), unit: 'minutes' as const };
  }, [props.value, DAYS_MS, HOURS, MINUTES]);

  const [state, setState] = React.useState({ unit, value: String(humanValue) });

  const milliseconds =
    Number(state.value) * (state.unit === 'days' ? DAYS_MS : state.unit == 'hours' ? HOURS : MINUTES);
  const error = React.useMemo(() => {
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) return null;
    if (milliseconds > 7 * DAYS_MS) return t('priceForm.feesApplyAfter7Days');
    return null;
  }, [milliseconds, DAYS_MS, t]);

  const debouncedOnChange = React.useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (value: number) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => props.onChange(value), 400);
    };
  }, [props.onChange]);

  const onIntervalChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = String(event.target.valueAsNumber || '');
      setState((s) => ({ ...s, value }));
      const newMilliseconds =
        Number(value) * (state.unit === 'days' ? DAYS_MS : state.unit === 'hours' ? HOURS : MINUTES);
      if (Number.isFinite(newMilliseconds) && newMilliseconds > 0 && newMilliseconds <= 7 * DAYS_MS) {
        debouncedOnChange(newMilliseconds);
      }
    },
    [state.unit, DAYS_MS, HOURS, MINUTES, debouncedOnChange],
  );

  const onUnitChange = React.useCallback(
    (newUnit: 'minutes' | 'hours' | 'days') => {
      setState((s) => ({ ...s, unit: newUnit }));
      const newMilliseconds =
        Number(state.value) * (newUnit === 'days' ? DAYS_MS : newUnit === 'hours' ? HOURS : MINUTES);
      if (Number.isFinite(newMilliseconds) && newMilliseconds > 0 && newMilliseconds <= 7 * DAYS_MS) {
        debouncedOnChange(newMilliseconds);
      }
    },
    [state.value, DAYS_MS, HOURS, MINUTES, debouncedOnChange],
  );

  return (
    <>
      <div className="flex gap-2">
        <Input
          id={props.id}
          type="number"
          className="basis-1/4"
          value={state.value}
          onChange={onIntervalChange}
        />
        <Select value={state.unit} onValueChange={onUnitChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="minutes">{t('priceForm.minutes')}</SelectItem>
            <SelectItem value="hours">{t('priceForm.hours')}</SelectItem>
            <SelectItem value="days">{t('priceForm.days')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {error ? <p className="text-sm text-red-500">{error}</p> : null}
    </>
  );
}
